import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { getAllLeaveApplicationsHR, updateLeaveApplicationSingle } from '../../../redux/features/leaveSlice';

const LeaveApprovalDialog = ({ open, onClose, leaveData, balanceLeaves, onSuccess, dispatchParams }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    status: '',
    remarks: '',
    leaveTypeId: leaveData?.leaveTypeId,
  });
  const [loading, setLoading] = useState(false);

  const { loadingDetails } = useSelector((state) => state.leave);

  useEffect(() => {
    if (leaveData) {
      setFormData({
        status: leaveData.hrStatus || '',
        remarks: leaveData.hrRemarks || '',
        leaveTypeId: leaveData.leaveTypeId,
      });
    }
  }, [leaveData]);

  const handleStatusChange = (e) => {
    setFormData({ ...formData, status: e.target.value });
  };

  const handleRemarksChange = (e) => {
    setFormData({ ...formData, remarks: e.target.value });
  };

  const handleLeaveTypeChange = (e) => {
    setFormData({ ...formData, leaveTypeId: e.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        leaveId: leaveData.leaveId,
        status: formData.status,
        comments: formData.remarks,
        leaveTypeId: formData.leaveTypeId,
      };
      const result = await dispatch(updateLeaveApplicationSingle(payload));
      if (result.payload?.status === 'success') {
        enqueueSnackbar(result.payload?.message || 'Leave application updated successfully', {
          variant: 'success',
        });
        dispatch(getAllLeaveApplicationsHR(dispatchParams));
        onClose();
      } else {
        enqueueSnackbar(result.payload?.message || 'Error updating leave application', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error updating leave:', error);
      enqueueSnackbar(error.message || 'Error updating leave application', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!leaveData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>Leave Application Details</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loadingDetails ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 350,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Employee & Leave Details */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Employee Information
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      Name
                    </TableCell>
                    <TableCell>{leaveData.empName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      Leave Type
                    </TableCell>
                    <TableCell>{leaveData.leaveType}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      From Date
                    </TableCell>
                    <TableCell>{leaveData.fromDate}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      To Date
                    </TableCell>
                    <TableCell>{leaveData.toDate}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      No. of Days
                    </TableCell>
                    <TableCell>{leaveData.noOfdays}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      Reason
                    </TableCell>
                    <TableCell>{leaveData.reason}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      RH
                    </TableCell>
                    <TableCell>{leaveData.reportingHead}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      RH Remarks
                    </TableCell>
                    <TableCell>{leaveData.hodRemarks}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 'bold' }}>
                      Applied On
                    </TableCell>
                    <TableCell>{leaveData.appliedOn}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {/* <Divider /> */}

            {/* Leave Balance */}
            {/* {balanceLeaves && balanceLeaves.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Leave Balance
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          Leave Type
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          Permissible
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          Taken
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          Remaining
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {balanceLeaves.map((leave) => (
                        <TableRow key={leave.leaveTypeId}>
                          <TableCell>{leave.leaveNameFull}</TableCell>
                          <TableCell align="center">
                            {leave.permissible ?? 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            {leave.leavesTaken}
                          </TableCell>
                          <TableCell align="center">
                            {leave.leavesRemaining}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )} */}

            <Divider />

            {/* Approval Form */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                HR Approval
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="leave-type-label">Leave Type</InputLabel>
                  <Select
                    labelId="leave-type-label"
                    id="leave-type-select"
                    value={formData.leaveTypeId || ''}
                    label="Leave Type"
                    onChange={handleLeaveTypeChange}
                  >
                    {balanceLeaves?.map((leave) => (
                      <MenuItem key={leave.leaveTypeId} value={leave.leaveTypeId}>
                        {leave.leaveNameFull} (Remaining: {leave.leavesRemaining})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status-select"
                    value={formData.status}
                    label="Status"
                    onChange={handleStatusChange}
                    required
                  >
                    <MenuItem value={1}>Approve</MenuItem>
                    <MenuItem value={2}>Reject</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Remarks"
                  placeholder="Add remarks for this leave application..."
                  value={formData.remarks}
                  onChange={handleRemarksChange}
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading || loadingDetails}>
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || loadingDetails || !formData.status}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveApprovalDialog;
